import { ReactNode, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { getUnixTime, startOfDay, subDays } from 'date-fns';
import './style.scss';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Order } from '../../core/types/transport/order';
import { Header } from '../../components/header';
import {
  useGetFullProjectQuery,
  useGetOrdersQuery,
} from '../../core/redux/transport';
import { useStateUrl } from '../../shared/hooks/params';
import { format, formatISODate } from '../../shared/logic/dates';
import { useGetAssignmentsQuery, useGetMyCompanyQuery } from '../../shared/redux/rental';
import { EquipmentAssignment } from '../../shared/types/equipmentAssignment';
import { MassOrderItem } from './partials/massOrderItem';
import { RentalOrderItem } from './partials/rentalOrderItem';
import { Checkbox } from '../../shared/components/checkbox';
import { useCompanyAccessWithQuery } from '../../shared/hooks/companyAccess';

const emptyTransport: Order[] = [];
const emptyRental: EquipmentAssignment[] = [];

interface Day {
  day: string;
  unix: number;
  items: ReactNode[];
}

export const OrdersPage = () => {
  const { id } = useParams<'id'>();
  const {
    access: {
      canAccessTransport,
      canAccessDieselTransport: canAccessDiesel,
      canAccessRental,
    },
    isCompanyAccessLoading: isPermissionLoading,
  } = useCompanyAccessWithQuery(() => useGetMyCompanyQuery());

  const [selection, setSelection] = useStateUrl<
    'Future' | 'Last7Days' | 'Last14Days' | 'Alle'
  >('tidsrom', 'Future', true);
  const [showDieselText, setShowDiesel] = useStateUrl<'true' | 'false'>(
    'diesel',
    'true',
    true,
  );
  const [showTransportText, setShowTransport] = useStateUrl<'true' | 'false'>(
    'transport',
    'true',
    true,
  );
  const [showRentalText, setShowRental] = useStateUrl<'true' | 'false'>(
    'innleie',
    'true',
    true,
  );
  const showTransport = canAccessTransport && showTransportText === 'true';
  const showRental = canAccessRental && showRentalText === 'true';
  const showDiesel = canAccessDiesel && showDieselText === 'true';
  const [sortAsc, setSortAsc] = useState<boolean>(false);

  const fromDate = useMemo(() => {
    const d = startOfDay(new Date());
    switch (selection) {
      case 'Last7Days':
        return formatISODate(subDays(d, 7));
      case 'Last14Days':
        return formatISODate(subDays(d, 14));
      case 'Alle':
        return undefined;
      default:
        return formatISODate(d);
    }
  }, [selection]);

  const {
    currentData: transportOrders = emptyTransport,
    isFetching: transportLoading,
  } = useGetOrdersQuery(
    {
      from: fromDate,
      projectId: id,
    },
    { skip: isPermissionLoading || !(showTransport || showDiesel) },
  );

  const { currentData: rentalOrders = emptyRental, isFetching: rentalLoading } = useGetAssignmentsQuery(
    {
      from: fromDate,
      projectId: id,
    },
    { skip: isPermissionLoading || !showRental },
  );

  const { data: project, isLoading: projectLoading } = useGetFullProjectQuery(
    id && canAccessTransport ? { id, date: formatISODate(new Date()) } : skipToken,
  );

  const isLoading = isPermissionLoading || transportLoading || rentalLoading || projectLoading;

  const orders = useMemo(() => {
    let days: Day[] = [];
    if (showTransport || showDiesel) {
      days = transportOrders
        .filter(
          (o) => (showDiesel && o.type === 'Diesel')
            || (showTransport && o.type !== 'Diesel'),
        )
        .reduce((acc: Day[], order: Order) => {
          const date = new Date(order.date);
          const day = format(date, 'EEEE do MMM yyyy');
          const unix = getUnixTime(date);
          const index = acc.findIndex((e) => e.day === day);
          const item = <MassOrderItem order={order} />;
          if (index === -1) {
            return [...acc, { unix, day, items: [item] }];
          }
          const arr = [...acc];
          arr[index].items.push(item);
          return arr;
        }, days);
    }

    if (showRental) {
      days = rentalOrders.reduce((acc: Day[], order: EquipmentAssignment) => {
        const date = new Date(order.from);
        const day = format(date, 'EEEE do MMM yyyy');
        const unix = getUnixTime(date);
        const index = acc.findIndex((e) => e.day === day);
        const item = <RentalOrderItem order={order} />;
        if (index === -1) {
          return [...acc, { unix, day, items: [item] }];
        }
        const arr = [...acc];
        arr[index].items.push(item);
        return arr;
      }, days);
    }

    if (sortAsc) {
      days.sort((a, b) => a.unix - b.unix);
    } else {
      days.sort((a, b) => b.unix - a.unix);
    }

    const items = days.map((day) => (
      <Box key={day.day}>
        <div className="day-header mt20 mb20">{day.day}</div>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {day.items}
        </Box>
      </Box>
    ));

    if (items.length <= 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            paddingTop: '4vh',
          }}
        >
          {isLoading && <CircularProgress />}
          {!isLoading && (
            <Typography sx={{ color: '#666', fontSize: 20 }}>
              Ingen bestillinger i denne perioden
            </Typography>
          )}
        </Box>
      );
    }
    return items;
  }, [
    showTransport,
    showRental,
    showDiesel,
    transportOrders,
    rentalOrders,
    isLoading,
    sortAsc,
  ]);

  return (
    <Box sx={{ flex: 1 }} className="orders-page">
      <Header page="order" showBack={!!id}>
        {id ? 'Prosjektdetaljer' : 'Mine Bestillinger'}
      </Header>
      <Box
        sx={{
          flex: 1,
          paddingX: 1,
          paddingY: '3vh',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '800px',
          }}
        >
          {isLoading && id && <div className="header">Laster...</div>}
          {!isLoading && !!project && (
            <>
              <div className="header">{`${project?.id} ${project?.projectName}`}</div>
              <div className="font-size-16 pt10 pb15">
                {`Ansvarlig: ${project?.responsible?.employeeNumber} ${project?.responsible?.fullName}`}
              </div>
            </>
          )}
          <div className="header">
            <span className="header-group">
              <ButtonGroup
                fullWidth
                variant="contained"
                aria-label="outlined primary button group"
              >
                <Button
                  variant={selection === 'Future' ? 'contained' : 'outlined'}
                  onClick={() => setSelection('Future')}
                >
                  Fremtidige
                </Button>
                <Button
                  variant={selection === 'Last7Days' ? 'contained' : 'outlined'}
                  onClick={() => setSelection('Last7Days')}
                >
                  siste 7 dager
                </Button>
                <Button
                  variant={
                    selection === 'Last14Days' ? 'contained' : 'outlined'
                  }
                  onClick={() => setSelection('Last14Days')}
                >
                  siste 14 dager
                </Button>
                <Button
                  variant={selection === 'Alle' ? 'contained' : 'outlined'}
                  onClick={() => setSelection('Alle')}
                >
                  Alle
                </Button>
              </ButtonGroup>
            </span>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                pt: 2,
                color: 'black',
              }}
            >
              {canAccessTransport && (
                <Checkbox
                  disabled={!showRental && !showDiesel}
                  checked={showTransport}
                  onChange={(v) => (showRental || showDiesel)
                    && setShowTransport(v ? 'true' : 'false')}
                  label="Vis transportbestillinger"
                />
              )}
              {canAccessRental && (
                <Checkbox
                  disabled={!showTransport && !showDiesel}
                  checked={showRental}
                  onChange={(v) => (showTransport || showDiesel)
                    && setShowRental(v ? 'true' : 'false')}
                  label="Vis internutleie"
                />
              )}
              {canAccessDiesel && (
                <Checkbox
                  disabled={!showTransport && !showRental}
                  checked={showDiesel}
                  onChange={(v) => (showTransport || showRental)
                    && setShowDiesel(v ? 'true' : 'false')}
                  label="Vis dieselbestillinger"
                />
              )}
              <IconButton
                aria-label="sort"
                onClick={() => setSortAsc(!sortAsc)}
              >
                {sortAsc ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
              </IconButton>
            </Box>
          </div>
          {orders}
        </Box>
      </Box>
    </Box>
  );
};
